import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Sparkles,
  Search,
  Users as UsersIcon,
  Check,
  X,
  FlaskConical,
} from "lucide-react";
import {
  pulseMetricsService,
  PulseUserRow,
} from "@core/services/PulseMetricsService";

const PAGE_SIZE = 50;

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const planColor = (plan: string, lsStatus: string | null): string => {
  if (plan === "free") return "bg-gray-500/20 text-gray-300";
  if (lsStatus === "active") return "bg-green-500/20 text-green-300";
  if (lsStatus === "cancelled" || lsStatus === "past_due")
    return "bg-red-500/20 text-red-300";
  return "bg-blue-500/20 text-blue-300";
};

export const UsersAdmin: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<PulseUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [hideTest, setHideTest] = useState(true);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { total, rows } = await pulseMetricsService.getUsers(
        search.trim() || null,
        PAGE_SIZE,
        page * PAGE_SIZE,
        hideTest,
      );
      setRows(rows);
      setTotal(total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }, [search, page, hideTest]);

  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
  }, [load]);

  const toggleTestFlag = async (row: PulseUserRow) => {
    try {
      await pulseMetricsService.setUserTestFlag(row.id, !row.is_test);
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, is_test: !row.is_test } : r,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erreur");
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
      <header className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-md font-semibold">DooDates Admin</span>
            </Link>
            <nav className="flex items-center gap-4">
              <Link
                to="/admin"
                className="text-xs text-gray-400 hover:text-white"
              >
                Support
              </Link>
              <Link
                to="/admin/roadmap"
                className="text-xs text-gray-400 hover:text-white"
              >
                Roadmap
              </Link>
              <Link
                to="/admin/analytics"
                className="text-xs text-gray-400 hover:text-white"
              >
                Analytics
              </Link>
              <Link
                to="/admin/pulse"
                className="text-xs text-gray-400 hover:text-white"
              >
                Pulse
              </Link>
              <Link
                to="/admin/users"
                className="text-xs text-white font-medium"
              >
                Users
              </Link>
              <Link to="/" className="text-xs text-gray-500 hover:text-white">
                Quitter l'admin
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <UsersIcon className="w-6 h-6 text-blue-400" />
          <div>
            <h1 className="text-2xl font-semibold">Utilisateurs</h1>
            <p className="text-sm text-gray-400">
              {total.toLocaleString("fr-FR")} résultat
              {total > 1 ? "s" : ""}
              {search ? ` pour "${search}"` : ""}
            </p>
          </div>
        </div>

        <div className="mb-4 flex gap-4 items-end">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Rechercher par email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
                const v = e.target.value.trim();
                if (v) setSearchParams({ search: v });
                else setSearchParams({});
              }}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
          </div>
          <label className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-400 cursor-pointer hover:bg-white/10">
            <input
              type="checkbox"
              checked={hideTest}
              onChange={(e) => {
                setHideTest(e.target.checked);
                setPage(0);
              }}
              className="w-4 h-4"
            />
            Masquer les tests
          </label>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-wide text-gray-400">
                <th className="px-4 py-2 text-left font-medium">Email</th>
                <th className="px-4 py-2 text-left font-medium w-24">Plan</th>
                <th className="px-4 py-2 text-right font-medium w-20">
                  Sondages
                </th>
                <th
                  className="px-4 py-2 text-right font-medium w-20"
                  title="Crédits IA restants / consommés (ledger réel, quota à vie pour anonyme/gratuit)"
                >
                  Crédits IA
                </th>
                <th
                  className="px-4 py-2 text-right font-medium w-16"
                  title="Messages envoyés au chat IA, 30 derniers jours"
                >
                  Chat 30j
                </th>
                <th
                  className="px-4 py-2 text-right font-medium w-16"
                  title="Générations IA appliquées, 30 derniers jours"
                >
                  IA appl. 30j
                </th>
                <th
                  className="px-4 py-2 text-right font-medium w-16"
                  title="Sauvegardes manuelles, 30 derniers jours"
                >
                  Manuel 30j
                </th>
                <th className="px-4 py-2 text-left font-medium w-28">
                  Inscrit
                </th>
                <th className="px-4 py-2 text-left font-medium w-28">
                  Dernière connexion
                </th>
                <th className="px-4 py-2 text-center font-medium w-20">Test</th>
                <th className="px-4 py-2 text-right font-medium w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    Chargement…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    Aucun utilisateur.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b border-white/5 hover:bg-white/5 ${r.is_test ? "opacity-60" : ""}`}
                  >
                    <td className="px-4 py-2 text-sm font-mono">
                      <Link
                        to={`/admin/users/${r.id}?email=${encodeURIComponent(r.email ?? "")}`}
                        className="text-white hover:text-blue-300 hover:underline"
                        title="Voir la timeline support"
                        data-testid="user-timeline-link"
                      >
                        {r.email}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${planColor(r.plan_type, r.lemonsqueezy_status)}`}
                      >
                        {r.plan_type}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-gray-300">
                      {r.polls_count}
                    </td>
                    <td className="px-4 py-2 text-right text-xs font-mono">
                      {r.ai_credits_remaining === null ? (
                        <span className="text-gray-500">—</span>
                      ) : (
                        <span
                          className={
                            r.ai_credits_remaining === 0
                              ? "text-red-400"
                              : "text-gray-300"
                          }
                          title={`${r.ai_credits_used} consommés`}
                        >
                          {r.ai_credits_remaining}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-gray-300 font-mono">
                      {r.ai_chat_messages_30d}
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-gray-300 font-mono">
                      {r.ai_generations_30d}
                    </td>
                    <td className="px-4 py-2 text-right text-xs text-gray-300 font-mono">
                      {r.manual_edits_30d}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400">
                      {formatDate(r.last_sign_in_at)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {r.is_test ? (
                        <FlaskConical className="w-4 h-4 text-amber-400 mx-auto" />
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => toggleTestFlag(r)}
                        className="text-xs px-2 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 flex items-center gap-1 ml-auto"
                        title={
                          r.is_test
                            ? "Retirer le flag test"
                            : "Marquer comme test"
                        }
                      >
                        {r.is_test ? (
                          <>
                            <X className="w-3 h-3" /> un-test
                          </>
                        ) : (
                          <>
                            <Check className="w-3 h-3" /> tag test
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              Page {page + 1} / {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="text-xs px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ← Préc.
              </button>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="text-xs px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Suiv. →
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
